import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ModalLoading } from 'components/ModalLoading';
import type { ProjectData } from 'gql/graphql';
import { cnMixSpace, Text } from 'uikit';

import { useCallback, useMemo } from 'react';

import { useRollbackWorkOrderChange } from './useRollbackWorkOrderChange';

type TRollbackWorkOrderChangeProps = {
    projectData: ProjectData | undefined | null;
    workOrderId: number;
    onClose: () => void;
    refreshProjectData: () => void;
    refreshWorkOrder: () => void;
    refreshVersionsData: () => void;
};

export const RollbackWorkOrderChange = ({
    projectData,
    workOrderId,
    onClose,
    refreshProjectData,
    refreshWorkOrder,
    refreshVersionsData,
}: TRollbackWorkOrderChangeProps) => {
    const currentWorkOrder = useMemo(
        () => projectData?.contractNavigation?.data?.workOrderNavs?.find((workOrder) => workOrder.id === workOrderId),
        [projectData?.contractNavigation?.data?.workOrderNavs, workOrderId],
    );

    const currentWorkOrderChangeInternalNumber = useMemo(
        () => currentWorkOrder?.workOrderChange?.workOrderChangeNumber,
        [currentWorkOrder?.workOrderChange?.workOrderChangeNumber],
    );

    const successHandler = useCallback(() => {
        refreshProjectData();
        refreshVersionsData();
        refreshWorkOrder();
        onClose();
    }, [refreshVersionsData, refreshProjectData, refreshWorkOrder, onClose]);

    const { makeRollbackHandler, abortRollback, isLoading } = useRollbackWorkOrderChange(workOrderId, successHandler);

    const confirmationText = useMemo(
        () => (
            <div
                className={cnMixSpace({
                    pH: 'xl',
                })}
            >
                <Text>
                    После возврата к предыдущей версии Наряд-Заказа все изменения, внесенные в текущую версию, будут
                    удалены. Изм. к НЗ № {currentWorkOrderChangeInternalNumber} будет присвоен статус “На согласовании”.
                </Text>
                <Text
                    className={cnMixSpace({
                        pT: 'l',
                    })}
                >
                    Вы уверены, что хотите внести изменения?
                </Text>
            </div>
        ),
        [currentWorkOrderChangeInternalNumber],
    );

    const onModalClose = useCallback(
        (isSubmitted: boolean) => {
            if (!isSubmitted) {
                onClose();
            }
        },
        [onClose],
    );

    return (
        <>
            <ConfirmationDialog
                isOpened
                onSubmit={makeRollbackHandler}
                onClose={onModalClose}
                textHeader="Смена версии наряд-заказа"
                textConfirmation={confirmationText}
                cancelButtonText="Не изменять"
                confirmButtonText="Изменить"
            />

            {isLoading && <ModalLoading title="Смена версии наряд-заказа" onClose={abortRollback} />}
        </>
    );
};

RollbackWorkOrderChange.displayName = 'RollbackWorkOrderChange';
